import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';

const API = '/api';

const statusBadge = s => ({
  pending:  'badge badge-amber',
  approved: 'badge badge-green',
  rejected: 'badge badge-red',
})[s] || 'badge badge-gray';

function RatingBar({ label, value, max = 5 }) {
  if (value === null || value === undefined) return null;
  // Auto-detect scale: if value > 5, it's on 0-10 scale
  const effectiveMax = value > 5 ? 10 : max;
  const pct = Math.min(100, (value / effectiveMax) * 100);
  return (
    <div style={{ marginBottom:6 }}>
      <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'var(--text-dim)', marginBottom:3 }}>
        <span>{label}</span><span>{value != null ? Number(value).toFixed(1) : '—'}/{max}</span>
      </div>
      <div style={{ height:4, background:'var(--border)', borderRadius:2, overflow:'hidden' }}>
        <div style={{ height:'100%', width:pct+'%', background:'var(--accent)', borderRadius:2 }} />
      </div>
    </div>
  );
}

function RatingModal({ rating, onClose, onAction, acting }) {
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState('');

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, width:'100%', maxWidth:520, overflow:'hidden' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'16px 20px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ fontSize:15, fontWeight:500, color:'var(--text-primary)' }}>Review rating</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-dim)', fontSize:20, cursor:'pointer' }}>×</button>
        </div>
        <div style={{ padding:'20px 24px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div style={{ background:'var(--bg-input)', borderRadius:8, padding:'10px 14px' }}>
              <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:3, textTransform:'uppercase' }}>Player</div>
              <div style={{ fontSize:13, color:'var(--text-primary)' }}>{rating.playerId?.slice(0,8)}...</div>
            </div>
            <div style={{ background:'var(--bg-input)', borderRadius:8, padding:'10px 14px' }}>
              <div style={{ fontSize:11, color:'var(--text-dim)', marginBottom:3, textTransform:'uppercase' }}>Match</div>
              <div style={{ fontSize:13, color:'var(--text-primary)' }}>{rating.matchId?.slice(0,8)}...</div>
            </div>
          </div>

          <div style={{ marginBottom:16 }}>
            <RatingBar label="Overall" value={rating.overall} max={5} />
            <RatingBar label="Pace" value={rating.pace} max={5} />
            <RatingBar label="Shooting" value={rating.shooting} max={5} />
            <RatingBar label="Passing" value={rating.passing} max={5} />
            <RatingBar label="Defending" value={rating.defending} max={5} />
            <RatingBar label="Stamina" value={rating.stamina} max={5} />
            <RatingBar label="Enjoyment" value={rating.enjoyment} max={5} />
          </div>

          {rating.notes && (
            <div style={{ background:'var(--bg-input)', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, color:'var(--text-dim)' }}>
              <div style={{ fontSize:11, textTransform:'uppercase', marginBottom:4 }}>Notes</div>
              {rating.notes}
            </div>
          )}

          {!action ? (
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-ghost" onClick={onClose}>
                {rating.status === 'pending' ? 'Cancel' : 'Close'}
              </button>
              {rating.status === 'pending' && <>
                <button className="btn btn-ghost" style={{ color:'var(--danger)' }} onClick={() => setAction('reject')}>Reject</button>
                <button className="btn btn-primary" onClick={() => onAction(rating.id, 'approve', '')}>Approve</button>
              </>}
            </div>
          ) : (
            <div>
              <div style={{ marginBottom:10 }}>
                <label style={{ fontSize:11, color:'var(--text-dim)', textTransform:'uppercase', display:'block', marginBottom:6 }}>Rejection reason</label>
                <textarea className="search-input" rows={3} value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="Why is this rating being rejected?" style={{ width:'100%', fontFamily:'inherit', resize:'vertical' }} />
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button className="btn btn-ghost" onClick={() => setAction(null)}>Back</button>
                <button className="btn btn-ghost" style={{ color:'var(--danger)' }} disabled={acting}
                  onClick={() => onAction(rating.id, 'reject', reason)}>
                  {acting ? 'Rejecting...' : 'Confirm reject'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MatchRatings({ toast }) {
  const { token } = useApp();
  const [ratings, setRatings]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [acting, setActing]       = useState(false);
  const [tab, setTab]             = useState('pending');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API + '/app/match-ratings/pending?status=' + tab + '&limit=50', {
        headers: { Authorization: 'Bearer ' + token },
      });
      const d = await res.json();
      setRatings(Array.isArray(d.data) ? d.data : []);
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setLoading(false); }
  }, [token, toast, tab]);

  useEffect(() => { load(); }, [load]);

  const handleAction = useCallback(async (id, action, reason) => {
    setActing(true);
    try {
      const res = await fetch(API + '/app/match-ratings/' + id + '/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ action, reason }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.message || 'Action failed');
      toast?.(action === 'approve' ? 'Rating approved and applied to player profile' : 'Rating rejected');
      setSelected(null);
      load();
    } catch (e) { toast?.(e.message, 'error'); }
    finally { setActing(false); }
  }, [token, toast, load]);

  return (
    <div>
      {selected && (
        <RatingModal rating={selected} onClose={() => setSelected(null)} onAction={handleAction} acting={acting} />
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:500, color:'var(--text-primary)', margin:0 }}>Match Ratings</h1>
          <p style={{ fontSize:13, color:'var(--text-dim)', marginTop:4 }}>Review and approve organizer-submitted player ratings</p>
        </div>
        <button className="btn btn-ghost" onClick={load} style={{ fontSize:12 }}>↻ Refresh</button>
      </div>

      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', marginBottom:24 }}>
        {['pending', 'approved', 'rejected'].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding:'9px 20px', fontSize:13, border:'none', cursor:'pointer', background:'none',
            color: tab===t ? 'var(--accent)' : 'var(--text-dim)',
            borderBottom: tab===t ? '2px solid var(--accent)' : '2px solid transparent',
            fontWeight: tab===t ? 600 : 400, fontFamily:'var(--font-body)', textTransform:'capitalize' }}>
            {t}
          </button>
        ))}
      </div>

      <div className="card" style={{ padding:0 }}>
        {loading ? (
          <div style={{ padding:48, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading ratings...</div>
        ) : ratings.length === 0 ? (
          <div style={{ padding:48, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>
            <div style={{ fontSize:32, marginBottom:12 }}>⭐</div>
            <div>No {tab} ratings to review</div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Match ID</th>
                  <th>Player ID</th>
                  <th>Overall</th>
                  <th>Pace</th>
                  <th>Shooting</th>
                  <th>Passing</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ratings.map(r => (
                  <tr key={r.id}>
                    <td style={{ fontSize:12, color:'var(--text-dim)' }}>{r.matchId?.slice(0,8)}...</td>
                    <td style={{ fontSize:12, color:'var(--text-dim)' }}>{r.playerId?.slice(0,8)}...</td>
                    <td><span style={{ fontWeight:600, color:'var(--accent)' }}>
  {r.overall != null ? (r.overall > 5 ? (r.overall/2).toFixed(1) : Number(r.overall).toFixed(1)) : '—'}
</span></td>
                    <td style={{ fontSize:12 }}>{r.pace ?? '—'}</td>
                    <td style={{ fontSize:12 }}>{r.shooting ?? '—'}</td>
                    <td style={{ fontSize:12 }}>{r.passing ?? '—'}</td>
                    <td><span className={statusBadge(r.status)}>{r.status}</span></td>
                    <td style={{ fontSize:12, color:'var(--text-dim)' }}>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                    <td>
                      <button className="btn btn-ghost" style={{ fontSize:11, padding:'3px 10px' }} onClick={() => setSelected(r)}>
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
